import React, { useContext, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GlobalContext } from "../../../Context/GlobalContext";
import { MdOutlineKeyboardBackspace } from "react-icons/md";
import {
  Calendar,
  RefreshCcw,
  ChevronLeft,
  ChevronRight,
  IndianRupee,
  CheckCircle2,
  Clock4,
  AlertTriangle,
  XCircle,
  CheckCircle,
  Download,
  Calculator,
} from "lucide-react";
import {
  ReceiptText,
  Wallet,
  Percent,
  BadgeIndianRupee,
  Truck,
} from "lucide-react";

import { Toaster, toast } from "react-hot-toast";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// ✅ New tab: settlement invoices
import SettlementInvoicesTab from "../../Orders/Settlement/SettlementInvoicesTab";
import PayOut from "../../Orders/Settlement/PayOut";

import useSettlement from "../../Services/OrderServices/SettlementServices/useSettlement";
import ReconSettlementInvoices from "./ReconSettlementInvoices";
import ReconPayOut from "./ReconPayOut";
import OrderChatDrawer from "../../common/OrderChatDrawer";

const NA = "N/A";

const toDateStr = (d) => {
  try {
    return new Date(d).toISOString().split("T")[0];
  } catch {
    return "";
  }
};

const fmt = (v) => {
  if (v === null || v === undefined || v === "") return NA;

  const num = Number(v);
  if (!Number.isNaN(num) && String(v).match(/^-?\d+(\.\d+)?$/))
    return num.toFixed(2);

  return String(v);
};
const roundAmount = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return Math.round(n); // ✅ integer value (no decimals)
};

const Badge = ({ tone = "slate", icon: Icon = CheckCircle2, children }) => {
  const map = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    yellow: "bg-amber-50 text-amber-700 border-amber-200",
    red: "bg-rose-50 text-rose-700 border-rose-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  };

  return (
    <span
      className={`inline-flex items-center justify-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${map[tone]}`}
    >
      <Icon size={14} />
      {children}
    </span>
  );
};

const paymentBadge = (v) => {
  const s = String(v || "")
    .trim()
    .toLowerCase();
  if (!s || s === "n/a")
    return (
      <Badge tone="slate" icon={Clock4}>
        {NA}
      </Badge>
    );
  if (s === "paid" || s === "success")
    return (
      <Badge tone="green" icon={CheckCircle2}>
        Paid
      </Badge>
    );
  if (s === "pending")
    return (
      <Badge tone="yellow" icon={Clock4}>
        Pending
      </Badge>
    );
  if (s === "failed")
    return (
      <Badge tone="red" icon={XCircle}>
        Failed
      </Badge>
    );
  return (
    <Badge tone="slate" icon={Clock4}>
      {String(v)}
    </Badge>
  );
};

const refundBadge = (v) => {
  const s = String(v || "")
    .trim()
    .toUpperCase();
  if (!s || s === "N/A")
    return (
      <Badge tone="slate" icon={Clock4}>
        {NA}
      </Badge>
    );
  if (s === "Y")
    return (
      <Badge tone="red" icon={AlertTriangle}>
        Refunded
      </Badge>
    );
  if (s === "N")
    return (
      <Badge tone="green" icon={CheckCircle2}>
        No
      </Badge>
    );
  return (
    <Badge tone="slate" icon={Clock4}>
      {String(v)}
    </Badge>
  );
};

const settlementBadge = (v) => {
  const s = String(v || "")
    .trim()
    .toUpperCase();
  if (!s || s === "N/A")
    return (
      <Badge tone="slate" icon={Clock4}>
        {NA}
      </Badge>
    );
  if (s === "Y")
    return (
      <Badge tone="green" icon={CheckCircle2}>
        Settled
      </Badge>
    );
  if (s === "N")
    return (
      <Badge tone="yellow" icon={Clock4}>
        Pending
      </Badge>
    );
  return (
    <Badge tone="slate" icon={Clock4}>
      {String(v)}
    </Badge>
  );
};
const ConfirmModal = ({
  open,
  title,
  desc,
  confirmText = "Confirm",
  danger = false,
  onClose,
  onConfirm,
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <div className="text-base font-semibold">{title}</div>
        {desc ? <div className="mt-2 text-sm text-gray-600">{desc}</div> : null}

        <div className="mt-4 flex justify-end gap-2">
          <button
            className="h-9 rounded-xl border px-3 text-sm"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className={`h-9 rounded-xl px-4 text-sm font-semibold text-white ${danger
                ? "bg-rose-600 hover:bg-rose-700"
                : "bg-[#01C37D] hover:bg-emerald-600"
              }`}
            onClick={onConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function SettlementReport() {
  const navigate = useNavigate();

  const {
    getAllSettlementsAnalytics,
    getAllSettlements,
    processSettlementAbhi,
  } = useSettlement();

  const { api } = useContext(GlobalContext);

  // ✅ userid comes from route path
  const params = useParams();
  const userid = params.clientId || params.userid || params.id;
  const Name = params.clientName || params.userid || params.id;

  const today = useMemo(() => toDateStr(new Date()), []);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  // ✅ Table tabs
  const [activeTab, setActiveTab] = useState("report"); // report | invoices | payout

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [confirmProcess, setConfirmProcess] = useState(false);
  const [processing, setProcessing] = useState(false);

  // ✅ analytics
  // ✅ analytics (UPDATED RESPONSE)
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analytics, setAnalytics] = useState({
    totalGrossAmount: null,
    orderAmount: null,
    unsettelementAmount: null,
    setteledAmount: null,
    platformCharge: null,
    taxOnPlatformCharge: null,
    delivery_fee: null,
    food_gst: null,
  });
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState("");

  // paginationz
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const totalPages = Math.max(1, Math.ceil((rows?.length || 0) / pageSize));
  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return (rows || []).slice(start, start + pageSize);
  }, [rows, page]);

  const fetchAnalytics = async () => {
    if (!userid) return;

    setAnalyticsLoading(true);

    try {
      const result = await getAllSettlementsAnalytics({
        userid,
        fromDate,
        toDate,
      });

      if (result.success) {
        const first = Array.isArray(result.data) ? result.data[0] : null;

        setAnalytics({
          totalGrossAmount: first?.totalGrossAmount ?? null,
          orderAmount: first?.orderAmount ?? null,
          unsettelementAmount: first?.unsettelementAmount ?? null,
          setteledAmount: first?.setteledAmount ?? null,
          platformCharge: first?.platformCharge ?? null,
          taxOnPlatformCharge: first?.taxOnPlatformCharge ?? null,
          delivery_fee: first?.delivery_fee ?? null,
          food_gst: first?.food_gst ?? null,
        });
      } else {
        setAnalytics({
          totalGrossAmount: null,
          orderAmount: null,
          unsettelementAmount: null,
          setteledAmount: null,
          platformCharge: null,
          taxOnPlatformCharge: null,
          delivery_fee: null,
          food_gst: null,
        });
      }
    } catch {
      setAnalytics({
        totalGrossAmount: null,
        orderAmount: null,
        unsettelementAmount: null,
        setteledAmount: null,
        platformCharge: null,
        taxOnPlatformCharge: null,
        delivery_fee: null,
        food_gst: null,
      });
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchReport = async () => {
    if (!userid) {
      toast.error("User ID missing in route path.");
      return;
    }

    if (new Date(fromDate) > new Date(toDate)) {
      toast.error("From Date cannot be after To Date.");
      return;
    }

    setLoading(true);

    fetchAnalytics(); // run alongside

    try {
      const result = await getAllSettlements({
        userid,
        fromDate,
        toDate,
      });

      if (result.success) {
        setRows(result.data.reverse());
        setPage(1);
      } else {
        setRows([]);
        setPage(1);
        toast.error(result.message);
      }
    } catch {
      setRows([]);
      setPage(1);
      toast.error("Failed to fetch settlements");
    } finally {
      setLoading(false);
    }
  };

  const processSettlement = async () => {
    if (!userid) {
      toast.error("User ID missing in route path.");
      return;
    }

    const roundedAmount = roundAmount(analytics?.settelementAmount);

    if (roundedAmount === null) {
      toast.error(
        "Settlement amount not available. Please click Filter first.",
      );
      return;
    }

    setConfirmProcess(false);
    setProcessing(true);

    try {
      const result = await processSettlementAbhi({
        userid,
        fromDate,
        toDate,
        amount: String(roundedAmount) + ".00",
      });

      if (result.success) {
        toast.success(result.message);
        fetchAnalytics();
        fetchReport();
      } else {
        toast.error(result.message);
      }
    } catch {
      toast.error("Error processing settlement.");
    } finally {
      setProcessing(false);
    }
  };

  // ✅ Export Excel (exports ALL rows fetched, not just current page)
  const exportExcel = () => {
    if (!rows || rows.length === 0) {
      toast.error("No data to export.");
      return;
    }

    try {
      const filename = `AllSettlements_${userid}_${fromDate}_to_${toDate}.xlsx`;

      // Export clean JSON:
      // - Keep raw values
      // - Don’t include React badges etc.
      const exportRows = rows.map((r) => ({
        id: r?.id ?? NA,
        fk_user_id: r?.fk_user_id ?? NA,
        fk_catalog_id: r?.fk_catalog_id ?? NA,
        order_id: r?.order_id ?? NA,
        order_date: r?.order_date ? formatDateTime(r.order_date) : NA,
        order_type: r?.order_type ?? NA,
        daily_order_number: r?.daily_order_number ?? NA,
        subtotal: r?.subtotal ?? NA,
        order_amount: r?.order_amount ?? NA,
        pay_amount: r?.pay_amount ?? NA,
        customer_mobile: r?.customer_mobile ?? NA,
        customer_name: r?.customer_name ?? NA,
        currency: r?.currency ?? NA,
        status: r?.status ?? NA,
        payment_status: r?.payment_status ?? NA,
        refund_status: r?.refund_status ?? NA,
        coupon_applied: r?.coupon_applied ?? NA,
        coupon_code: r?.coupon_code ?? NA,
        tax_sgst: r?.tax_sgst ?? NA,
        tax_cgst: r?.tax_cgst ?? NA,
        order_charges: r?.order_charges ?? NA,
        order_tax_gst: r?.order_tax_gst ?? NA,
        taxOnCommission: r?.taxOnCommission ?? NA,
        chaafoCommission: r?.chaafoCommission ?? NA,
        settelment_value: r?.settelment_value ?? NA,
        delivery_charges: r?.delivery_charges ?? NA,
        settelment_status: r?.settelment_status ?? NA,
      }));

      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "AllSettlements");

      const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([buffer], { type: "application/octet-stream" }),
        filename,
      );

      toast.success("Excel downloaded.");
    } catch {
      toast.error("Failed to export excel.");
    }
  };

  const tableHeaders = [
    "ID",
    // "User ID",
    // "Catalog ID",
    "Order ID",
    "Date and time",
    "Type",
    "Subtotal",
    "Order Amount",
    "Payable Amount",
    "Customer Mobile",
    "Customer Name",
    "Currency",
    // "Order Status",
    "Payment Status",
    "Refund Status",
    "Coupon Applied",
    // "Coupon Code",
    "SGST",
    "CGST",
    "Order Charges",
    "Order GST",
    "Tax on Commission",
    "Platform Commission",
    "Settlement Value",
    "Delivery Charges",
    "Settlement Status",
  ];
    function formatDateTime(dateString) {
  const date = new Date(dateString);

  const formattedDate = date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const formattedTime = date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true, // change to false if you want 24-hour format
  });

  return `${formattedDate}, ${formattedTime}`;
}

  return (
    <div className="p-3 lg:p-5">
      <Toaster position="top-right" />

      <div className="mb-3">
        <div style={{ cursor: 'pointer' }} className="text-2xl mb-5" onClick={() => navigate("/admin/recon")} ><MdOutlineKeyboardBackspace /></div>
        <h1 className="text-xl font-semibold">Recon Settlements Report</h1>
        <p className="text-sm text-gray-500">
          Client Name:{" "}
          <span className="font-semibold text-gray-800">{Name || NA}</span>
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-4 flex flex-wrap gap-2">
        {[
          { key: "report", label: "Settlement Report" },
          { key: "invoices", label: "Invoices" },
          { key: "payout", label: "Pay Out" },
        ].map((t) => {
          const isActive = activeTab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              className={`h-10 rounded-xl border px-4 text-sm font-semibold transition ${isActive
                  ? "bg-[#01C37D] text-white border-[#01C37D]"
                  : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Filters + Export (Report tab only) */}
      {activeTab === "report" && (
        <div className="mb-3 grid gap-2 sm:grid-cols-2 md:grid-cols-5">
          <label className="text-sm">
            <div className="mb-1 flex items-center gap-1 text-xs text-gray-500">
              <Calendar size={14} /> From
            </div>
            <input
              type="date"
              className="h-10 w-full rounded-xl border px-3 text-sm outline-none"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </label>

          <label className="text-sm">
            <div className="mb-1 flex items-center gap-1 text-xs text-gray-500">
              <Calendar size={14} /> To
            </div>
            <input
              type="date"
              className="h-10 w-full rounded-xl border px-3 text-sm outline-none"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </label>

          {/* Filter button */}
          <div className="flex items-end">
            <button
              className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50 disabled:opacity-50"
              onClick={fetchReport}
              disabled={loading}
            >
              <RefreshCcw size={16} />
              {loading ? "Loading..." : "Filter"}
            </button>
          </div>


          {/* ✅ Export button (next to filter) */}
          <div className="flex items-end md:col-span-1">
            <button
              className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[#01C37D] px-3 text-sm font-semibold text-white hover:bg-emerald-600 disabled:opacity-50"
              onClick={exportExcel}
              disabled={loading || !rows || rows.length === 0}
              title={
                !rows || rows.length === 0
                  ? "No data to export"
                  : "Export to Excel"
              }
            >
              <Download size={16} />
              Export Excel
            </button>
          </div>
        </div>
      )}

      {/* Analytics Cards (Report tab only) */}
      {activeTab === "report" && (
        <div className="mb-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-8">
          {/* Order Amount */}
          <div className="rounded-2xl border bg-gradient-to-br from-blue-50 to-white p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-[11px] font-semibold text-blue-600">
                  Order Amount
                </div>
                <div className="mt-1 text-xl font-bold text-blue-900 flex items-center gap-1">
                  <IndianRupee size={16} className="opacity-80" />
                  {analyticsLoading ? "Loading..." : fmt(analytics.orderAmount)}
                </div>
              </div>
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
                <BadgeIndianRupee size={18} />
              </span>
            </div>
            <div className="mt-2 text-[11px] text-blue-600/70">
              Total order value
            </div>
          </div>
          {/* Food Gst Amount  */}
          <div className="rounded-2xl border bg-gradient-to-br from-rose-50 to-white p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-[11px] font-semibold text-rose-700">
                  Food GST
                </div>
                <div className="mt-1 text-xl font-bold text-rose-900 flex items-center gap-1">
                  <IndianRupee size={16} className="opacity-80" />
                  {analyticsLoading ? "Loading..." : fmt(analytics.food_gst)}
                </div>
              </div>
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-rose-100 text-rose-700">
                <Calculator size={18} />
              </span>
            </div>
            <div className="mt-2 text-[11px] text-rose-700/70">
              Total GST deduction
            </div>
          </div>
          {/* Total Gross Amount */}
          <div className="rounded-2xl border bg-gradient-to-br from-slate-50 to-white p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-[11px] font-semibold text-slate-500">
                  Total Gross Amount
                </div>
                <div className="mt-1 text-xl font-bold text-slate-900">
                  {analyticsLoading
                    ? "Loading..."
                    : fmt(analytics.totalGrossAmount)}
                </div>
              </div>
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                <ReceiptText size={18} />
              </span>
            </div>
            <div className="mt-2 text-[11px] text-slate-500">
              All gross across selected range
            </div>
          </div>
          {/* Already Settled Amount */}
          {/* Already Settled Amount */}
          <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50/70 to-white p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-[11px] font-semibold text-emerald-600">
                  Settled Amount
                </div>
                <div className="mt-1 text-xl font-bold text-emerald-700 flex items-center gap-1">
                  <IndianRupee size={16} className="opacity-70" />
                  {analyticsLoading
                    ? "Loading..."
                    : fmt(analytics.setteledAmount)}
                </div>
              </div>

              <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600">
                <CheckCircle size={18} />
              </span>
            </div>

            <div className="mt-2 text-[11px] text-emerald-600/70">
              Amount already settled
            </div>
          </div>

          {/* Settlement Amount */}
          <div className="rounded-2xl border border-emerald-300 bg-gradient-to-br from-emerald-100 to-white p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-[11px] font-semibold text-emerald-700">
                  Settlement Amount
                </div>
                <div className="mt-1 text-xl font-bold text-emerald-900 flex items-center gap-1">
                  <IndianRupee size={16} className="opacity-80" />
                  {analyticsLoading
                    ? "Loading..."
                    : fmt(analytics.unsettelementAmount)}
                </div>
              </div>

              <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-200 text-emerald-700">
                <Wallet size={18} />
              </span>
            </div>

            <div className="mt-2 text-[11px] text-emerald-700/70">
              Net payable settlement
            </div>
          </div>

          {/* Platform Charge */}
          <div className="rounded-2xl border bg-gradient-to-br from-violet-50 to-white p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-[11px] font-semibold text-violet-700">
                  Platform Charge
                </div>
                <div className="mt-1 text-xl font-bold text-violet-900 flex items-center gap-1">
                  <IndianRupee size={16} className="opacity-80" />
                  {analyticsLoading
                    ? "Loading..."
                    : fmt(analytics.platformCharge)}
                </div>
              </div>
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-violet-100 text-violet-700">
                <Percent size={18} />
              </span>
            </div>
            <div className="mt-2 text-[11px] text-violet-700/70">
              Commission / platform fee
            </div>
          </div>

          {/* Tax On Platform Charge */}
          <div className="rounded-2xl border bg-gradient-to-br from-amber-50 to-white p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-[11px] font-semibold text-amber-700">
                  Tax on Platform Charge
                </div>
                <div className="mt-1 text-xl font-bold text-amber-900 flex items-center gap-1">
                  <IndianRupee size={16} className="opacity-80" />
                  {analyticsLoading
                    ? "Loading..."
                    : fmt(analytics.taxOnPlatformCharge)}
                </div>
              </div>
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
                <IndianRupee size={18} />
              </span>
            </div>
            <div className="mt-2 text-[11px] text-amber-700/70">
              GST / tax applied on platform fee
            </div>
          </div>

          {/* Delivery Fee */}
          <div className="rounded-2xl border bg-gradient-to-br from-rose-50 to-white p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-[11px] font-semibold text-rose-700">
                  Delivery Fee
                </div>
                <div className="mt-1 text-xl font-bold text-rose-900 flex items-center gap-1">
                  <IndianRupee size={16} className="opacity-80" />
                  {analyticsLoading
                    ? "Loading..."
                    : fmt(analytics.delivery_fee)}
                </div>
              </div>
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-rose-100 text-rose-700">
                <Truck size={18} />
              </span>
            </div>
            <div className="mt-2 text-[11px] text-rose-700/70">
              Total delivery charges
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {activeTab === "report" && (
        <div className="rounded-2xl border bg-white">
          <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
            <div className="text-sm font-semibold">Results</div>

            {/* Pagination */}
            <div className="flex items-center gap-2 text-xs">
              <span className="text-gray-500">
                Total:{" "}
                <span className="font-semibold text-gray-800">
                  {rows.length || 0}
                </span>
              </span>

              <button
                className="h-8 w-8 rounded-lg border hover:bg-gray-50 disabled:opacity-40"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                title="Prev"
              >
                <ChevronLeft size={16} className="mx-auto" />
              </button>

              <span className="min-w-[90px] text-center">
                Page <span className="font-semibold">{page}</span> /{" "}
                <span className="font-semibold">
                  {Math.max(1, Math.ceil((rows?.length || 0) / pageSize))}
                </span>
              </span>

              <button
                className="h-8 w-8 rounded-lg border hover:bg-gray-50 disabled:opacity-40"
                onClick={() =>
                  setPage((p) =>
                    Math.min(
                      Math.max(1, Math.ceil((rows?.length || 0) / pageSize)),
                      p + 1,
                    ),
                  )
                }
                disabled={
                  page >= Math.max(1, Math.ceil((rows?.length || 0) / pageSize))
                }
                title="Next"
              >
                <ChevronRight size={16} className="mx-auto" />
              </button>
            </div>
          </div>

          <div className="max-h-[70vh] overflow-auto">
            <table className="min-w-full text-left text-xs">
              <thead className="sticky top-0 z-10 bg-[#01C37D] text-white">
                <tr>
                  {tableHeaders.map((h) => (
                    <th
                      key={h}
                      className="px-2 py-2 font-semibold whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {/* If no data -> show N/A row */}
                {!loading && (!rows || rows.length === 0) && (
                  <tr className="border-t">
                    {tableHeaders.map((h) => (
                      <td key={h} className="px-2 py-2 text-gray-500">
                        {NA}
                      </td>
                    ))}
                  </tr>
                )}

                {/* Rows */}
                {pagedRows.map((r, idx) => (
                  <tr key={`${r?.id ?? "row"}-${idx}`} className="border-t">
                    <td className="px-2 py-1.5">{fmt(r?.id)}</td>
                    {/* <td className="px-2 py-1.5">{fmt(r?.fk_user_id)}</td> */}
                    {/* <td className="px-2 py-1.5">{fmt(r?.fk_catalog_id)}</td> */}
                    <td className="px-2 py-1.5 underline" onClick={() => {
                      setSelectedOrderId(r?.order_id || r?.orderid);
                      setChatDrawerOpen(true);
                    }} style={{ cursor: "pointer" }}
                    ><b>{(r?.order_id)}</b></td>
                     <td className="px-2 py-1.5 font-thin"><b>{formatDateTime(r?.order_date)}</b></td>
                    <td className="px-2 py-1.5">
                      {fmt(r?.order_type)}
                    </td>
                    <td className="px-2 py-1.5">{fmt(r?.subtotal)}</td>
                    <td className="px-2 py-1.5">{fmt(r?.order_amount)}</td>
                    <td className="px-2 py-1.5">{fmt(r?.pay_amount)}</td>
                    <td className="px-2 py-1.5">{fmt(r?.customer_mobile)}</td>
                    <td className="px-2 py-1.5">{fmt(r?.customer_name)}</td>
                    <td className="px-2 py-1.5">{fmt(r?.currency)}</td>
                    {/* <td className="px-2 py-1.5">{fmt(r?.status)}</td> */}

                    {/* badges */}
                    <td className="px-2 py-1.5">
                      {paymentBadge(r?.payment_status)}
                    </td>
                    <td className="px-2 py-1.5">
                      {refundBadge(r?.refund_status)}
                    </td>

                    <td className="px-2 py-1.5">{fmt(r?.coupon_applied)}</td>
                    {/* <td className="px-2 py-1.5">{fmt(r?.coupon_code)}</td> */}
                    <td className="px-2 py-1.5">{fmt(r?.tax_sgst)}</td>
                    <td className="px-2 py-1.5">{fmt(r?.tax_cgst)}</td>
                    <td className="px-2 py-1.5">{fmt(r?.order_charges)}</td>
                    <td className="px-2 py-1.5">{fmt(r?.order_tax_gst)}</td>
                    <td className="px-2 py-1.5">{fmt(r?.taxOnCommission)}</td>
                    <td className="px-2 py-1.5">{fmt(r?.chaafoCommission)}</td>
                    <td className="px-2 py-1.5">{fmt(r?.settelment_value)}</td>
                    <td className="px-2 py-1.5">{fmt(r?.delivery_charges)}</td>

                    <td className="px-2 py-1.5">
                      {settlementBadge(r?.settelment_status)}
                    </td>
                  </tr>
                ))}

                {loading && (
                  <tr className="border-t">
                    <td
                      colSpan={tableHeaders.length}
                      className="px-4 py-6 text-center text-gray-500"
                    >
                      Loading…
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="border-t px-3 py-2 text-[11px] text-gray-500">
            Note: Data loads only when you click{" "}
            <span className="font-semibold">Filter</span>. Excel exports current
            fetched table data.
          </div>
        </div>
      )}

      {/* Invoices */}
      {activeTab === "invoices" && (
        <ReconSettlementInvoices
          userID={String(userid || "")}
          defaultFromDate={fromDate}
          defaultToDate={toDate}
        />
      )}

      {/* Pay Out */}
      {activeTab === "payout" && (
        <div className="rounded-2xl border bg-white p-4">
          <ReconPayOut />
        </div>
      )}
      <OrderChatDrawer
        open={chatDrawerOpen}
        onClose={() => setChatDrawerOpen(false)}
        orderId={selectedOrderId}
      />

    </div>
  );
}
